/**
 * /code-grants - code-granted workspaces running out (OCP-911, OCP-917).
 *
 * Every managed workspace provisioned from an invite / prepaid code with its
 * grant end, grace state, the day it shuts down (paused, read-only, AGB 12.3
 * export window - never Community, that is the self-host edition), the end
 * of that export window and the reminder stages already mailed. Default view
 * = the "act now" list (grant ends within the chosen horizon, plus every
 * workspace already in grace); "all" shows every code workspace. The extend
 * action lives on the tenant detail (Code grant panel), same REST route as
 * the MCP tool ocp_extend_grant. Same data: GET /v1/admin/code-grants /
 * MCP ocp_list_expiring_grants.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { GrantStatePill, StageChips, type CodeGrant } from '../components/CodeGrantPanel';
import { api, ApiError } from '../lib/api';
import { fmtDate } from '../lib/format-time';

interface ListResponse {
  items: CodeGrant[];
  total: number;
  grace_days: number;
  /** OCP-917 - lifecycle.hard_delete_grace_days: the 12.3 export window after the shutdown. */
  export_days: number;
}

const HORIZONS = [7, 30, 90] as const;

export function AdminCodeGrantsPage() {
  const [horizon, setHorizon] = useState<number | 'all'>(30);
  const [data, setData] = useState<ListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const q = horizon === 'all' ? '' : `?within_days=${horizon}`;
      setData(await api<ListResponse>(`/v1/admin/code-grants${q}`));
    } catch (err) {
      setError(err instanceof ApiError ? `Could not load code grants (HTTP ${err.status}).` : 'Could not load code grants.');
    }
  }, [horizon]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="mx-auto max-w-6xl">
      <h1 className="text-2xl font-semibold tracking-tight">Code grants</h1>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Workspaces running on an invite / prepaid code. Reminders 30 / 14 / 7 / 1 days before the end, then
        {data ? ` ${data.grace_days} days` : ' the'} grace with the full plan (license.code_grace_days), then the workspace
        shuts down: paused, read-only with the closed page, its data exportable in the portal under Backups for
        {data ? ` ${data.export_days} days` : ' the 12.3 window'} (AGB 12.3). A subscription of the account covers the
        workspace instead and brings a shut-down one back. Extend on the tenant page. Scratch workspaces are outside
        this lifecycle.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2 text-sm" role="group" aria-label="Horizon">
        {HORIZONS.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setHorizon(d)}
            aria-pressed={horizon === d}
            className={`rounded px-3 py-1 ring-1 ring-inset ${horizon === d ? 'bg-brand-500 text-white ring-brand-500' : 'ring-gray-300 dark:ring-gray-700'}`}
          >
            ending in {d} days + grace
          </button>
        ))}
        <button
          type="button"
          onClick={() => setHorizon('all')}
          aria-pressed={horizon === 'all'}
          className={`rounded px-3 py-1 ring-1 ring-inset ${horizon === 'all' ? 'bg-brand-500 text-white ring-brand-500' : 'ring-gray-300 dark:ring-gray-700'}`}
        >
          all code workspaces
        </button>
      </div>

      {error && (
        <div role="alert" className="mt-4 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {data && data.items.length === 0 && (
        <div className="mt-6 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
          No code grant in this view.
        </div>
      )}

      {data && data.items.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/60 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">Workspace</th>
                <th className="px-3 py-2">Account</th>
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">State</th>
                <th className="px-3 py-2">Grant ends</th>
                <th className="px-3 py-2">Shuts down</th>
                <th className="px-3 py-2">Export until</th>
                <th className="px-3 py-2">Mails</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {data.items.map((g) => (
                <tr key={g.tenant_id} data-testid="code-grant-row">
                  <td className="px-3 py-2">
                    <Link to={`/tenants/${g.tenant_id}`} className="text-brand-500 hover:underline">{g.subdomain}</Link>
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{g.customer_email ?? '-'}</td>
                  <td className="px-3 py-2">
                    <span className="font-mono text-xs">{g.code ?? '-'}</span>
                    {g.tier && <span className="ml-1 text-xs text-gray-500">{g.tier} / {g.seats ?? 'unlimited'}</span>}
                  </td>
                  <td className="px-3 py-2"><GrantStatePill state={g.state} /></td>
                  <td className="px-3 py-2">{g.grant_ends_at ? `${fmtDate(g.grant_ends_at)} (${g.days_until_end} d)` : 'lifetime'}</td>
                  <td className="px-3 py-2">{g.shuts_down_at ? fmtDate(g.shuts_down_at) : '-'}</td>
                  <td className="px-3 py-2">{g.export_until ? fmtDate(g.export_until) : '-'}</td>
                  <td className="px-3 py-2"><StageChips grant={g} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.total > data.items.length && (
            <p className="px-3 py-2 text-xs text-gray-500">Showing {data.items.length} of {data.total}.</p>
          )}
        </div>
      )}
    </div>
  );
}
